import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import type { Rights } from '../lib/session.js';
import { dateTime, num } from '../lib/format.js';
import {
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
} from '../components/ui.js';
import { TemplateEditor } from './TemplateEditor.js';
import { RaceLanguages } from './RaceLanguages.js';
import { RacePotential, RaceRecommendations } from './RacePotential.js';
import { SegmentBuilder } from './SegmentBuilder.js';
import { Broadcasts } from './Broadcasts.js';
import { TemplateMaintenance } from './TemplateMaintenance.js';
import { RaceSelect } from '../components/RaceSelect.js';
import '../screens-2a.css';

/**
 * Odeslat rozesílku: průvodce od závodu po odeslání (závod → šablona → obsah → komu → kontrola).
 * Nic nového nevymýšlí, jen vede člověka po krocích a skládá hotové části (editor šablon, překlady,
 * dosah, segmenty, náhled, schválení).
 */

interface TemplateFamily {
  base: string;
  brand: string;
  name: string;
  languages: Array<{ language: string; published: boolean }>;
}
interface RaceOption {
  raceId: string;
  name: string;
  brand: string;
  dateStart: string | null;
  phase?: 'past' | 'ongoing' | 'upcoming' | 'undated';
  cancelled?: boolean;
  buyers: number;
  watchdogs: number;
}
interface SegmentItem {
  code: string;
  name: string;
  memberCount: number;
  popis: string;
}
interface ListItem {
  id: string;
  name: string;
  members: number;
}
interface Check {
  recipients: number;
  recentlyMailed: Array<{ code: string; contacts: number; lastDaysAgo: number; share: number }>;
  alreadyBoughtRace: number | null;
  sameRaceBroadcasts: Array<{ id: number; name: string; status: string; daysAgo: number | null }>;
  deliverability: {
    days: number;
    sent: number;
    bouncedPct: number;
    complaintPct: number;
    unsubscribedPct: number;
  };
  warnings: string[];
}
interface Preview {
  total: number;
  willSend: number;
  reasons: Array<{ status: string; reason: string; count: number }>;
  languages: Array<{ language: string; count: number }>;
  globalHold: string | null;
}

const STEPS = ['Závod', 'Šablona', 'Obsah', 'Komu', 'Kontrola'] as const;
const LANGS: Array<[string, string]> = [
  ['en', 'angličtina'],
  ['de', 'němčina'],
  ['it', 'italština'],
  ['pl', 'polština'],
  ['sk', 'slovenština'],
  ['hu', 'maďarština'],
];

type Audience = { kind: 'segment'; code: string } | { kind: 'static_list'; id: string } | null;

export function ComposeBroadcast({ rights }: { rights: Rights }): JSX.Element {
  const [step, setStep] = useState(0);
  const [family, setFamily] = useState<TemplateFamily | null>(null);
  const [editKey, setEditKey] = useState<string | undefined>();
  const [raceId, setRaceId] = useState('');
  const [audience, setAudience] = useState<Audience>(null);
  const [audienceTab, setAudienceTab] = useState<'segment' | 'list' | 'new'>('segment');
  const [name, setName] = useState('');
  const [broadcastId, setBroadcastId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canWrite = rights.write('communication');

  const races = useQuery({
    queryKey: ['segment-builder-options'],
    queryFn: () => api.get<{ races: RaceOption[] }>('/api/segments/builder-options'),
    staleTime: 60_000,
  });
  const race = races.data?.races.find((r) => r.raceId === raceId);
  const brand = family?.brand ?? race?.brand ?? 'TGP';
  const segments = useQuery({
    queryKey: ['segments'],
    queryFn: () => api.get<{ items: SegmentItem[] }>('/api/segments'),
  });
  const lists = useQuery({
    queryKey: ['static-lists'],
    queryFn: () => api.get<{ items: ListItem[] }>('/api/static-lists'),
  });
  const audienceLabel =
    audience?.kind === 'segment'
      ? segments.data?.items.find((s) => s.code === audience.code)?.name
      : audience?.kind === 'static_list'
        ? lists.data?.items.find((l) => l.id === audience.id)?.name
        : undefined;
  const audienceParams: Record<string, string> =
    audience?.kind === 'segment'
      ? { segmentCode: audience.code }
      : audience?.kind === 'static_list'
        ? { staticListId: audience.id }
        : {};

  const check = useQuery({
    queryKey: ['audience-check', brand, audience, raceId],
    enabled: step === 4 && !!audience && !!family,
    queryFn: () =>
      api.get<Check>(
        `/api/broadcasts/audience-check${qs({ brand, ...audienceParams, ...(raceId ? { raceId } : {}) })}`,
      ),
  });

  const prepare = useMutation({
    mutationFn: async () => {
      if (!family || !audience) throw new Error('Chybí šablona nebo skupina příjemců.');
      const body = {
        name: name.trim() || `${family.name}${race ? ` · ${race.name}` : ''}`,
        brand: family.brand,
        templateBase: family.base,
        fallbackLanguage: 'en',
        audience:
          audience.kind === 'segment'
            ? { kind: 'segment', segmentCode: audience.code }
            : { kind: 'static_list', staticListId: audience.id },
        windowMode: 'recipient_window',
        canaryEnabled: true,
        raceId: raceId || null,
      };
      const view = broadcastId
        ? await api.put<{ id: number }>(`/api/broadcasts/${broadcastId}`, body)
        : await api.post<{ id: number }>('/api/broadcasts', body);
      setBroadcastId(view.id);
      return api.post<Preview>(`/api/broadcasts/${view.id}/preview`, {});
    },
    onSuccess: () => setError(null),
    onError: (e: Error) => setError(e.message),
  });
  const preview = prepare.data;

  const canNext =
    step === 0 ||
    (step === 1 && family !== null) ||
    step === 2 ||
    (step === 3 && audience !== null);

  const chooseFamily = (f: TemplateFamily): void => {
    setFamily(f);
    setEditKey(`${f.base}_${f.brand}_cs`);
  };

  return (
    <>
      <PageHeader
        title="Odeslat rozesílku"
        description="Postup krok za krokem: vyberte závod, šablonu (nebo připravte nový e-mail), upravte obsah a překlady, určete komu poslat a zkontrolujte dosah. Nic neodejde, dokud rozesílku výslovně neschválíte."
      />

      <nav className="howflow" aria-label="Kroky rozesílky">
        <ol>
          {STEPS.map((s, i) => (
            <li key={s}>
              <button
                type="button"
                className={`stepchip${i === step ? ' on' : ''}`}
                disabled={i > 1 && !family}
                onClick={() => setStep(i)}
              >
                <i>{i + 1}</i>
                {s}
              </button>
            </li>
          ))}
        </ol>
      </nav>

      {error ? <ErrorState title="Něco se nepovedlo" error={new Error(error)} /> : null}

      {step === 0 ? (
        <>
          <Panel
            title="1. K jakému závodu rozesílka patří?"
            subtitle="Podle závodu se nabídnou jeho šablony, ukážou data o vstupenkách a skupiny žhavých kontaktů. Závod se zapíše k rozesílce a zobrazí v Odeslaných. Nepovinné."
          >
            <Field label="Závod">
              <RaceSelect
                races={races.data?.races ?? []}
                value={raceId}
                emptyLabel="— bez závodu —"
                onChange={(id) => {
                  setRaceId(id);
                  setFamily(null);
                }}
              />
            </Field>
          </Panel>
          {raceId ? <RaceTickets raceId={raceId} /> : null}
          {raceId ? (
            <RaceLanguages raceId={raceId} canWrite={canWrite} canApprove={canWrite} />
          ) : null}
        </>
      ) : null}

      {step === 1 ? (
        <TemplateStep
          brand={brand}
          raceId={raceId}
          raceName={race?.name}
          family={family}
          canWrite={canWrite}
          onChoose={(f) => chooseFamily(f)}
          onCreated={(f) => {
            chooseFamily(f);
            setStep(2);
          }}
        />
      ) : null}

      {step === 2 ? (
        <>
          <Panel
            title="3. Obsah e-mailu"
            subtitle={
              family
                ? `Upravujete „${family.name}“ (${family.brand}). Bloky se přidávají, přesouvají a upravují přímo v editoru: klikněte na část e-mailu v náhledu, nebo na blok v seznamu. Obrázky vyberete tlačítkem Galerie u pole s adresou obrázku.`
                : 'Nejdřív vyberte šablonu v kroku 2.'
            }
          >
            {family ? (
              <TranslatePanel
                family={family}
                raceId={raceId}
                canWrite={canWrite}
                onOpen={(lang) => setEditKey(`${family.base}_${family.brand}_${lang}`)}
                activeKey={editKey}
              />
            ) : null}
          </Panel>
          <TemplateEditor rights={rights} {...(editKey ? { initialKey: editKey } : {})} />
          <TemplateMaintenance canWrite={canWrite} />
        </>
      ) : null}

      {step === 3 ? (
        <>
          <Panel
            title="4. Komu rozesílku pošleme?"
            subtitle="Vyberte uložený segment nebo seznam, nebo vytvořte nový. Dosah vidíte hned."
          >
            <div className="seg" role="tablist" aria-label="Zdroj příjemců">
              {(
                [
                  ['segment', 'Segment'],
                  ['list', 'Uložený seznam'],
                  ['new', 'Nový segment'],
                ] as const
              ).map(([id, text]) => (
                <button
                  key={id}
                  role="tab"
                  aria-selected={audienceTab === id}
                  className={audienceTab === id ? 'on' : ''}
                  onClick={() => setAudienceTab(id)}
                >
                  {text}
                </button>
              ))}
            </div>
            {audienceTab === 'segment' ? (
              <Field label="Segment">
                <select
                  value={audience?.kind === 'segment' ? audience.code : ''}
                  aria-label="Segment"
                  onChange={(e) =>
                    setAudience(e.target.value ? { kind: 'segment', code: e.target.value } : null)
                  }
                >
                  <option value="">— vyberte —</option>
                  {(segments.data?.items ?? []).map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name} · {num(s.memberCount)} kontaktů
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            {audienceTab === 'list' ? (
              <Field label="Uložený seznam" hint="Například skupina žhavých z přehledu níže.">
                <select
                  value={audience?.kind === 'static_list' ? audience.id : ''}
                  aria-label="Uložený seznam"
                  onChange={(e) =>
                    setAudience(e.target.value ? { kind: 'static_list', id: e.target.value } : null)
                  }
                >
                  <option value="">— vyberte —</option>
                  {(lists.data?.items ?? []).map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} · {num(l.members)} adres
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            {audienceTab === 'new' && rights.can('edit_catalogue') ? (
              <SegmentBuilder
                onSaved={() => {
                  void segments.refetch();
                  setAudienceTab('segment');
                }}
              />
            ) : null}
            {audienceTab === 'new' && !rights.can('edit_catalogue') ? (
              <p className="note">Nový segment zakládá ten, kdo spravuje katalog zpráv.</p>
            ) : null}
            {audience ? (
              <p>
                Vybráno: <b>{audienceLabel ?? '…'}</b>
              </p>
            ) : null}
          </Panel>
          {raceId ? <RaceRecommendations raceId={raceId} canMakeList={canWrite} /> : null}
          {raceId ? (
            <RacePotential raceId={raceId} canMakeList={canWrite} />
          ) : (
            <p className="note">
              Vyberete-li v kroku 1 závod, ukážeme tu i skupiny žhavých a teplých kontaktů k němu.
            </p>
          )}
        </>
      ) : null}

      {step === 4 ? (
        <>
          <Panel title="5. Kontrola před odesláním" subtitle="Shrnutí, upozornění a dosah.">
            <dl className="kv">
              <dt>Závod</dt>
              <dd>{race ? race.name : 'bez závodu'}</dd>
              <dt>Šablona</dt>
              <dd>
                {family
                  ? `${family.name} (${family.brand}, jazyky ${family.languages.map((l) => l.language).join(', ')})`
                  : '—'}
              </dd>
              <dt>Komu</dt>
              <dd>{audienceLabel ?? '—'}</dd>
            </dl>
            <Field label="Název rozesílky" hint="Když nic nenapíšete, použije se šablona a závod.">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={family ? `${family.name}${race ? ` · ${race.name}` : ''}` : ''}
              />
            </Field>

            {check.isLoading ? <LoadingState what="kontrolu" /> : null}
            {check.data ? (
              <div className="stack" data-testid="compose-check">
                <p>
                  Ve skupině je <b>{num(check.data.recipients)}</b> kontaktů.
                </p>
                {check.data.warnings.length === 0 ? (
                  <StatusBadge tone="ok" icon="check">
                    nic zvláštního k upozornění
                  </StatusBadge>
                ) : (
                  <ul>
                    {check.data.warnings.map((w) => (
                      <li key={w}>
                        <StatusBadge tone="warn" icon="alert">
                          {w}
                        </StatusBadge>
                      </li>
                    ))}
                  </ul>
                )}
                {check.data.recentlyMailed.length > 0 ? (
                  <p className="note">
                    Posledních 30 dní dostali:{' '}
                    {check.data.recentlyMailed
                      .map(
                        (r) =>
                          `${r.code} (${num(r.contacts)}, naposledy před ${r.lastDaysAgo} dny)`,
                      )
                      .join(' · ')}
                  </p>
                ) : null}
                <p className="note">
                  Doručitelnost značky za {check.data.deliverability.days} dní:{' '}
                  {check.data.deliverability.sent > 0
                    ? `odesláno ${num(check.data.deliverability.sent)}, odskoky ${check.data.deliverability.bouncedPct} %, stížnosti ${check.data.deliverability.complaintPct} %, odhlášení ${check.data.deliverability.unsubscribedPct} %.`
                    : 'zatím žádná odeslaná data.'}
                </p>
              </div>
            ) : null}

            <div className="row">
              <button
                type="button"
                className="btn pri"
                disabled={!canWrite || prepare.isPending || !family || !audience}
                onClick={() => prepare.mutate()}
                data-testid="compose-prepare"
              >
                {broadcastId ? 'Přepočítat příjemce' : 'Připravit rozesílku a spočítat příjemce'}
              </button>
            </div>
            {!canWrite ? <p className="note">Rozesílky připravuje oddělení Mailing.</p> : null}

            {preview ? (
              <div className="stack" data-testid="compose-preview">
                <p>
                  Zprávu dostane <b>{num(preview.willSend)}</b> z {num(preview.total)} lidí.
                  {preview.globalHold ? ` Celá rozesílka je zadržená: ${preview.globalHold}.` : ''}
                </p>
                {preview.reasons.length > 0 ? (
                  <p className="note">
                    Nedostanou:{' '}
                    {preview.reasons.map((r) => `${r.reason} ${num(r.count)}`).join(' · ')}
                  </p>
                ) : null}
                {preview.languages.length > 0 ? (
                  <p className="note">
                    Jazyky:{' '}
                    {preview.languages.map((l) => `${l.language} ${num(l.count)}`).join(' · ')}
                  </p>
                ) : null}
              </div>
            ) : null}
          </Panel>

          {broadcastId ? (
            <Panel
              title="Zkouška a schválení"
              subtitle="Rozesílku teď můžete vyzkoušet na testovací adresu a schválit. Schvalování a plánování má stejná pravidla jako jinde (dva lidé, potvrzení názvem)."
            >
              <Broadcasts rights={rights} initialOpenId={broadcastId} />
            </Panel>
          ) : null}
        </>
      ) : null}

      <div className="row dlg-actions">
        <button
          type="button"
          className="btn"
          disabled={step === 0}
          onClick={() => setStep(Math.max(0, step - 1))}
        >
          ← Zpět
        </button>
        {step < STEPS.length - 1 ? (
          <button
            type="button"
            className="btn pri"
            disabled={!canNext}
            onClick={() => setStep(step + 1)}
          >
            Další krok →
          </button>
        ) : null}
      </div>
    </>
  );
}

/** Krok 2: šablony k závodu, ostatní a založení nového e-mailu (prázdného nebo kopie). */
function TemplateStep({
  brand,
  raceId,
  raceName,
  family,
  canWrite,
  onChoose,
  onCreated,
}: {
  brand: string;
  raceId: string;
  raceName: string | undefined;
  family: TemplateFamily | null;
  canWrite: boolean;
  onChoose: (f: TemplateFamily) => void;
  onCreated: (f: TemplateFamily) => void;
}): JSX.Element {
  const client = useQueryClient();
  const q = useQuery({
    queryKey: ['broadcast-template-families', brand, raceId],
    queryFn: () =>
      api.get<{ forRace: TemplateFamily[]; others: TemplateFamily[] }>(
        `/api/broadcast-templates${qs({ brand, ...(raceId ? { raceId } : {}) })}`,
      ),
  });
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [copyFrom, setCopyFrom] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.post<{ base: string; name: string; brand: string }>('/api/broadcast-templates', {
        brand,
        name: newName,
        ...(copyFrom ? { copyFromBase: copyFrom } : {}),
        ...(raceId ? { raceId } : {}),
      }),
    onSuccess: (r) => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['broadcast-template-families'] });
      onCreated({
        base: r.base,
        brand: r.brand,
        name: r.name,
        languages: [{ language: 'cs', published: false }],
      });
    },
    onError: (e: Error) => setError(e.message),
  });
  const all = [...(q.data?.forRace ?? []), ...(q.data?.others ?? [])];

  const card = (t: TemplateFamily): JSX.Element => (
    <button
      key={t.base}
      type="button"
      className={`card-choice${family?.base === t.base ? ' on' : ''}`}
      onClick={() => onChoose(t)}
    >
      <b>{t.name}</b>
      <br />
      <small className="muted">
        {t.languages
          .map((l) => `${l.language}${l.published ? '' : ' (koncept)'}`)
          .sort()
          .join(', ')}
      </small>
    </button>
  );

  return (
    <Panel
      title="2. Jakou šablonu pošleme?"
      subtitle={
        raceName
          ? `Šablony k závodu ${raceName}. Můžete vybrat hotovou, nebo připravit nový e-mail.`
          : 'Vyberte hotovou šablonu rozesílky, nebo připravte nový e-mail.'
      }
      action={
        canWrite ? (
          <button type="button" className="btn pri" onClick={() => setCreating(!creating)}>
            + Nový e-mail
          </button>
        ) : undefined
      }
    >
      {creating ? (
        <div className="stack" data-testid="new-email">
          <Field label="Název e-mailu" hint="Například „Monza: poslední volná místa“.">
            <input value={newName} onChange={(e) => setNewName(e.target.value)} />
          </Field>
          <Field
            label="Začít z"
            hint="Kopie hotového e-mailu ušetří práci, prázdný je jen text a patička."
          >
            <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
              <option value="">— prázdný e-mail —</option>
              {all.map((t) => (
                <option key={t.base} value={t.base}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          {error ? <ErrorState title="Založení se nepovedlo" error={new Error(error)} /> : null}
          <div className="row">
            <button
              type="button"
              className="btn pri"
              disabled={!newName.trim() || create.isPending}
              onClick={() => create.mutate()}
            >
              Založit a upravit obsah
            </button>
            <button type="button" className="btn" onClick={() => setCreating(false)}>
              Zrušit
            </button>
          </div>
        </div>
      ) : null}

      {q.isLoading ? <LoadingState what="šablony" /> : null}
      {q.isError ? <ErrorState title="Šablony se nepodařilo načíst" error={q.error} /> : null}
      {q.data && raceId ? (
        q.data.forRace.length === 0 ? (
          <p className="note">
            K tomuto závodu zatím nejsou žádné šablony. Založte nový e-mail, nebo vyberte jednu z
            ostatních níže.
          </p>
        ) : (
          <div className="grid2">{q.data.forRace.map(card)}</div>
        )
      ) : null}
      {q.data && q.data.others.length > 0 ? (
        <details open={!raceId || q.data.forRace.length === 0}>
          <summary>
            {raceId
              ? `Ostatní šablony (${q.data.others.length})`
              : `Šablony (${q.data.others.length})`}
          </summary>
          <div className="grid2">{q.data.others.map(card)}</div>
        </details>
      ) : null}
      {q.data && all.length === 0 ? (
        <EmptyState title="Zatím tu není žádná šablona rozesílky">
          Založte první tlačítkem „Nový e-mail“.
        </EmptyState>
      ) : null}
    </Panel>
  );
}

/** Překlady vybrané šablony: návrhy do dalších jazyků a přepínání jazyka v editoru. */
function TranslatePanel({
  family,
  raceId,
  canWrite,
  onOpen,
  activeKey,
}: {
  family: TemplateFamily;
  raceId: string;
  canWrite: boolean;
  onOpen: (lang: string) => void;
  activeKey: string | undefined;
}): JSX.Element {
  const client = useQueryClient();
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const have = new Map(family.languages.map((l) => [l.language, l.published]));
  const translate = useMutation({
    mutationFn: () =>
      api.post<{
        created: Array<{ key: string; untranslated: number }>;
        skipped: Array<{ key: string; reason: string }>;
      }>('/api/broadcast-templates/translate', {
        brand: family.brand,
        base: family.base,
        languages: picked,
        ...(raceId ? { raceId } : {}),
      }),
    onSuccess: (r) => {
      setError(null);
      setNote(
        `Vytvořeno ${r.created.length} návrhů překladu${r.skipped.length > 0 ? `, přeskočeno ${r.skipped.length} (${r.skipped.map((s) => s.reason).join(', ')})` : ''}. Zkontrolujte je v editoru: texty, které se nepodařilo přeložit, zůstaly česky.`,
      );
      for (const c of r.created) {
        const lang = c.key.split('_').pop() ?? '';
        if (!have.has(lang)) family.languages.push({ language: lang, published: false });
      }
      void client.invalidateQueries({ queryKey: ['broadcast-template-families'] });
      void client.invalidateQueries({ queryKey: ['template-list'] });
    },
    onError: (e: Error) => setError(e.message),
  });
  return (
    <div className="stack" data-testid="translate-panel">
      <div className="row" role="group" aria-label="Jazyk v editoru">
        <b>Upravit jazyk:</b>
        {[...have.keys()].sort().map((lang) => (
          <button
            key={lang}
            type="button"
            className={`btn${activeKey === `${family.base}_${family.brand}_${lang}` ? ' pri' : ''}`}
            onClick={() => onOpen(lang)}
          >
            {lang}
            {have.get(lang) ? '' : ' (koncept)'}
          </button>
        ))}
      </div>
      {canWrite ? (
        <>
          <p>
            <b>Přeložit do dalšího jazyka</b> (z české verze; návrh pak upravíte a publikujete):
          </p>
          <div className="row" role="group" aria-label="Jazyky překladu">
            {LANGS.map(([code, text]) => (
              <label key={code} className="row">
                <input
                  type="checkbox"
                  checked={picked.includes(code)}
                  onChange={(e) =>
                    setPicked(
                      e.target.checked ? [...picked, code] : picked.filter((x) => x !== code),
                    )
                  }
                />
                <span>
                  {text}
                  {have.has(code) ? ' (už existuje)' : ''}
                </span>
              </label>
            ))}
            <button
              type="button"
              className="btn pri"
              disabled={picked.length === 0 || translate.isPending}
              onClick={() => translate.mutate()}
            >
              Vytvořit návrhy překladů
            </button>
          </div>
          {note ? (
            <StatusBadge tone="ok" icon="check">
              {note}
            </StatusBadge>
          ) : null}
          {error ? <ErrorState title="Překlad se nepovedl" error={new Error(error)} /> : null}
        </>
      ) : null}
    </div>
  );
}

/** Data o vstupenkách závodu: dostupnost a ceny po kategoriích a co už je prodáno (jen čtení). */
function RaceTickets({ raceId }: { raceId: string }): JSX.Element {
  const q = useQuery({
    queryKey: ['race', raceId],
    queryFn: () =>
      api.get<{
        race: { dateStart: string | null; timezone: string; salesStatus: string };
        availability: Array<{ category: string; availability: string; price: string | null }>;
        ticketsByCategory: Array<{
          category: string | null;
          ticketStatus: string | null;
          n: number;
        }>;
        stats: { tickets: number; customers: number } | null;
      }>(`/api/races/${encodeURIComponent(raceId)}`),
  });
  const sold = new Map<string, number>();
  for (const t of q.data?.ticketsByCategory ?? [])
    sold.set(t.category ?? '—', (sold.get(t.category ?? '—') ?? 0) + t.n);
  return (
    <Panel
      title="Data o vstupenkách"
      subtitle="Aktuální dostupnost a ceny z e-shopu. Hodí se do textu rozesílky."
    >
      {q.isLoading ? <LoadingState what="data o vstupenkách" /> : null}
      {q.isError ? <ErrorState title="Data se nepodařilo načíst" error={q.error} /> : null}
      {q.data ? (
        <>
          <p className="note">
            Začátek {q.data.race.dateStart ? dateTime(q.data.race.dateStart) : 'neznámý'}, prodej:{' '}
            {q.data.race.salesStatus}. Prodáno {num(q.data.stats?.tickets ?? 0)} vstupenek{' '}
            {q.data.stats ? `${num(q.data.stats.customers)} zákazníkům` : ''}.
          </p>
          {q.data.availability.length === 0 ? (
            <EmptyState title="E-shop zatím neposlal dostupnost">
              Dostupnost přijde z e-shopu; zatím se nedá ukázat.
            </EmptyState>
          ) : (
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Kategorie</th>
                    <th>Dostupnost</th>
                    <th className="n">Cena</th>
                    <th className="n">Prodáno</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.availability.map((a) => (
                    <tr key={a.category}>
                      <td>{a.category}</td>
                      <td>{a.availability}</td>
                      <td className="n">{a.price ?? '—'}</td>
                      <td className="n">{num(sold.get(a.category) ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </Panel>
  );
}
